import {
  detectMediaColumns,
  mediaColumnsTakenOver,
} from '@/lib/utils/image-url-detection';

export const smartOfferValues = {
  name: ['NOM', 'NAME', 'NOM DU PRODUIT'],
  optionColor: ['COULEUR', 'Couleur', 'COLOR', 'COLOUR'],
  optionSize: ['TAILLE', 'Taille', 'SIZE'],
  optionOther: ['AUTRE', 'OTHER'],
  url: ['URL'],
  gtin: ['GTIN', 'VARIANT', 'ID SHOPIFY', 'ID PRODUIT SHOPIFY', 'VAR ID'],
  sku: ['SKU', 'REF PRODUIT'],
  description: ['DESCRIPTION', 'Description'],
  gender: ['GENRE', 'GENDER'],
  stock: ['STOCK', 'Stock'],
  deliveryDelays: ['DELAIS', 'DELIVERY'],
  isReturnable: ['RETOUR', 'IS RETURNABLE'],
  shippingPrice: [
    'FRAIS DE PORT À DOMICILE',
    'Frais de port à domicile',
    'FRAIS DE PORT',
    'Frais de port',
    'FRAIS DE LIVRAISON',
    'SHIPPING',
    'Shipping Price',
  ],
  weight: ['POIDS', 'WEIGHT'],
  buyingPrice: [
    'PRIX ACHAT',
    'BUYING PRICE',
    'RETROCESSION',
    'PRIX DE RETROCESSION HT',
    'Prix de rétrocession ht',
    'BTOB PRICE',
    'PRIX B2B',
    'B2B PRICE',
  ],
  retailPrice: [
    'PRIX PUBLIC',
    'PRIX PUBLIC CONSEILLE TTC',
    'Prix public conseillé ttc',
    'RETAIL PRICE',
    'PUBLIC PRICE',
  ],
  choosePrice: [
    'PRIX CHOOSE',
    'PRIX APRES REMISE TTC',
    'Prix après remise ttc',
    'PRIX CHOOSE TTC RECOMMANDE',
    'Prix choose ttc recommandé',
    'CHOOSE PRICE',
    'CHOOSEPRICE',
  ],
  choosePriceEEE: [
    'PRIX CHOOSE EEE',
    'ECO-PART',
    'ECO-TAXE',
    'Éco-Taxe',
    'ECO PART',
    'ECO TAXE',
    'DEEE',
  ],
  chooseMargin: ['MARGE CHOOSE', 'MARGE', 'Marge', 'CHOOSE MARGIN'],
  originCountry: [
    'PAYS',
    'PAYS DE FABRICATION',
    'Pays de fabrication',
    'COUNTRY',
    'Origin Country',
  ],
  summary: ['RESUME', 'SUMMARY'],
  cgu: ['CGU'],
  faqBeforeOrder: ['FAQ BEFORE ORDER'],
  faqAfterOrder: ['FAQ AFTER ORDER'],
  voucherExpirationDate: ['VALIDITE', 'VOUCHER EXPIRATION DATE'],
  category: ['CATEGORIE', 'CATEGORY'],
  discount: ['DISCOUNT', 'DISCOUNT (%) RECOMMANDE', 'Discount (%) recommandé'],
  faqStep: ['ETAPE', 'STEP'],
  width: ['Width', 'Largeur'],
  height: ['Height', 'Hauteur'],
  length: ['Length', 'Longueur'],
  product_variant_should_be_included: [
    'Inclure ce produit dans la vente ?',
    'Include this product in the sale?',
  ],
  units_per_pack: ['COLISAGE', 'UNITS PER PACK', 'PCB'],
} satisfies Record<string, string[]>;

export const mappingSalesPurposes = {
  FR: Object.values(smartOfferValues).flat(),
  EN: Object.values(smartOfferValues).flat(),
  US: Object.values(smartOfferValues).flat(),
} as const;

const smartOfferValuesWhitelist = new Set(
  Object.values(smartOfferValues)
    .flat()
    .map((column) => normalizeColumnName(column))
);

/**
 * Root prefixes of columns that are NOT e-merchandising data.
 * A column is excluded if its normalized form starts with any of these prefixes.
 * This avoids listing every variant (e.g. "Tva", "TVA (%)", "Tva (à modifier si besoin)").
 */
const emerchBlacklistPrefixes = [
  'position',
  'classement',
  'type',
  'id deal',
  'issue',
  'inclus',
  'potentiel',
  'tva',
  'nom produit',
  'seo',
  'product category',
  'variant sku',
  'variant id',
  'prix publique',
  'prix de retrocession',
  'frais de livraison',
  'mondial relay',
  'offre',
  'franco',
  'prix public',
  'prix choose',
  'date',
  'cgu',
  'etape',
  'step',
  'nom',
  'gtin',
  'sku',
  'url',
  'description',
  'marge',
  'discount',
];

/**
 * Normalize a column name for robust comparison:
 * - trim whitespace
 * - strip accents (NFD + remove combining marks)
 * - lowercase
 * - strip parenthetical content: "Tva (à modifier)" → "Tva"
 * - replace underscores, hyphens, and punctuation with spaces
 * - collapse multiple spaces into one
 */
function normalizeColumnName(value: string) {
  return value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\(.*?\)/g, '')
    .replace(/[_\-.,;:!?/\\*]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function mediaColumnsTakenOverByImport(
  columns: string[],
  rows: Record<string, unknown>[]
): Set<string> {
  const cells = rows.map((row) => {
    const unmapped = row._unmapped_data as Record<string, unknown> | undefined;
    return columns.map((column) => {
      const value = unmapped?.[column];
      return typeof value === 'string' ? value : '';
    });
  });
  const detection = detectMediaColumns({ headers: columns, rows: cells });
  return new Set(
    mediaColumnsTakenOver(detection).map((column) => column.header)
  );
}

function matchesBlacklistPrefix(normalizedColumn: string) {
  return emerchBlacklistPrefixes.some((prefix) =>
    normalizedColumn.startsWith(prefix)
  );
}

/**
 * Returns true if at least one row has a non-empty value for the given column
 * inside `_unmapped_data`.
 */
function columnHasData(
  rows: Record<string, unknown>[],
  column: string
): boolean {
  return rows.some((row) => {
    const unmapped = row._unmapped_data as Record<string, unknown> | undefined;
    const value = unmapped?.[column];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
}

export function detectEmerchColumns(
  ignoredColumns: string[] = [],
  rows: Record<string, unknown>[] = []
) {
  const seenColumns = new Set<string>();
  const takenOverByImport = mediaColumnsTakenOverByImport(ignoredColumns, rows);

  return ignoredColumns.filter((column) => {
    const normalizedColumn = normalizeColumnName(column);

    if (!normalizedColumn) {
      return false;
    }

    if (smartOfferValuesWhitelist.has(normalizedColumn)) {
      return false;
    }

    if (matchesBlacklistPrefix(normalizedColumn)) {
      return false;
    }

    if (takenOverByImport.has(column)) {
      return false;
    }

    if (seenColumns.has(normalizedColumn)) {
      return false;
    }

    // Skip columns where no row has any actual data
    if (rows.length > 0 && !columnHasData(rows, column)) {
      return false;
    }

    seenColumns.add(normalizedColumn);
    return true;
  });
}
